// Pure helpers for Database → Access Logs: reason labels, the filters kept in
// the URL, and the request body for POST /databaseService/accessLog/list
// (authnull-service internal/database/accesslog). No React here, so it is
// covered by plain jest tests (dbAccessLogModel.test.js).

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 500;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// Time range presets. "custom" means explicit from/to in the URL.
export const DEFAULT_RANGE = "24h";
export const RANGES = [
  { value: "1h", label: "Last hour", ms: HOUR },
  { value: "24h", label: "Last 24 hours", ms: DAY },
  { value: "3d", label: "Last 3 days", ms: 3 * DAY },
  { value: "7d", label: "Last 7 days", ms: 7 * DAY },
  { value: "30d", label: "Last 30 days", ms: 30 * DAY },
];
export const CUSTOM_RANGE = "custom";

// reasonCode → label. Anything not listed is shown as the code itself.
export const REASON_LABELS = {
  token_refused: "Token refused",
  policy_error: "Policy error",
  policy_block: "Blocked by policy",
  policy_allow: "Allowed by policy",
  mfa_approved: "MFA approved",
  mfa_denied: "MFA denied",
  mfa_timeout: "MFA timed out",
  approval_denied: "Approval denied",
  condition_denied: "Condition not met",
  no_policy_allow: "No policy: allowed",
  no_policy_deny: "No policy: denied",
  error: "Error",
  other: "Other",
};

export const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label }));

/**
 * The words for a reason code. "other" shows the free-text reason when there
 * is one; an unknown code is shown as itself; no code at all is "—".
 */
export function reasonLabel(code, reason) {
  const c = String(code ?? "").trim();
  if (!c) return reason ? String(reason) : "—";
  if (c === "other") return reason ? String(reason) : REASON_LABELS.other;
  return REASON_LABELS[c] || c;
}

export const DECISION_OPTIONS = [
  { value: "allowed", label: "Allowed" },
  { value: "denied", label: "Denied" },
];

export const DB_TYPE_LABELS = { postgres: "Postgres", postgresql: "Postgres", mysql: "MySQL", mariadb: "MariaDB" };
export const dbTypeLabel = (t) => {
  const k = String(t ?? "").trim().toLowerCase();
  return k ? DB_TYPE_LABELS[k] || t : "";
};

// Text filters, by URL key. The URL key is the API field name.
export const TEXT_FILTERS = ["email", "dbUser", "database", "clientIp"];

const isIso = (s) => typeof s === "string" && s.trim() !== "" && !Number.isNaN(Date.parse(s));
const posInt = (s) => {
  const n = Number(s);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * URL query → filters. Accepts a URLSearchParams or a query string. Bad values
 * fall back to the defaults instead of reaching the API (which would 400).
 */
export function parseFilters(search) {
  const p = search instanceof URLSearchParams ? search : new URLSearchParams(search || "");
  const get = (k) => (p.get(k) || "").trim();

  const out = { email: "", dbUser: "", database: "", clientIp: "", decision: "", reasonCode: "" };
  TEXT_FILTERS.forEach((k) => {
    out[k] = get(k);
  });
  const decision = get("decision");
  out.decision = DECISION_OPTIONS.some((o) => o.value === decision) ? decision : "";
  out.reasonCode = get("reasonCode");

  const from = get("from");
  const to = get("to");
  const range = get("range");
  if (isIso(from) || isIso(to)) {
    out.range = CUSTOM_RANGE;
    out.from = isIso(from) ? new Date(from).toISOString() : "";
    out.to = isIso(to) ? new Date(to).toISOString() : "";
  } else {
    out.range = RANGES.some((r) => r.value === range) ? range : DEFAULT_RANGE;
    out.from = "";
    out.to = "";
  }

  out.page = posInt(get("page")) || 1;
  const size = posInt(get("pageSize"));
  out.pageSize = size ? Math.min(size, MAX_PAGE_SIZE) : DEFAULT_PAGE_SIZE;
  return out;
}

/** filters → URL query (URLSearchParams). Defaults are left out to keep links short. */
export function filtersToParams(f) {
  const p = new URLSearchParams();
  TEXT_FILTERS.forEach((k) => {
    const v = String(f[k] || "").trim();
    if (v) p.set(k, v);
  });
  if (f.decision) p.set("decision", f.decision);
  if (f.reasonCode) p.set("reasonCode", f.reasonCode);
  if (f.range === CUSTOM_RANGE) {
    if (f.from) p.set("from", f.from);
    if (f.to) p.set("to", f.to);
  } else if (f.range && f.range !== DEFAULT_RANGE) {
    p.set("range", f.range);
  }
  if (f.page && f.page > 1) p.set("page", String(f.page));
  if (f.pageSize && f.pageSize !== DEFAULT_PAGE_SIZE) p.set("pageSize", String(f.pageSize));
  return p;
}

/**
 * filters → request body. Only set fields are sent; orgId never is (the org is
 * the session's). A preset window ends at `now`; from is inclusive, to exclusive.
 */
export function buildPayload(f, now = Date.now()) {
  const body = {};
  TEXT_FILTERS.forEach((k) => {
    const v = String(f[k] || "").trim();
    if (v) body[k] = v;
  });
  if (f.decision) body.decision = f.decision;
  if (f.reasonCode) body.reasonCode = f.reasonCode;
  if (f.range === CUSTOM_RANGE) {
    if (f.from) body.from = f.from;
    if (f.to) body.to = f.to;
  } else {
    const r = RANGES.find((x) => x.value === f.range) || RANGES.find((x) => x.value === DEFAULT_RANGE);
    body.from = new Date(now - r.ms).toISOString();
    body.to = new Date(now).toISOString();
  }
  body.page = f.page || 1;
  body.pageSize = Math.min(f.pageSize || DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);
  return body;
}
